<template>
  <Layout class="g-wrapper g-vertical-block">
    <div class="c-accountmana g-main-wrapper">
      <account-manage-filter
        placeholder="账号ID/账号名称/计划主ID"
        :data-source="dataSource"
        :select-list="selectList"
        @on-account-export="accountExport(params)"
        @on-enable="batchChangeStatus(1)"
        @on-disable="batchChangeStatus(0)"
        @on-import="importExcel"
        @on-template="templateDown"
        @on-account-disable="openAccountDisableModal"
        @on-search="getData"
      >
        <div style="display: flex">
          <ElButton type="primary" @click="onAddAccount">新建账号</ElButton>
          <el-upload
            :action="uploadUrl"
            :show-file-list="false"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :with-credentials="true"
            :on-change="handleUploadChange"
            accept="application/vnd.ms-excel,.xls,.xlsx"
          >
            <el-button style="margin-left: 10px" :loading="clearFileImportLoading" type="primary">一键清理</el-button>
          </el-upload>
          <el-button type="primary" style="margin-left: 10px" @click="openMaterialConfigModal">媒体素材管理账号配置</el-button>
        </div>
      </account-manage-filter>

      <Viewport v-model="tableHeight">
        <account-manage-table
          :data="tableList"
          :loading="loading"
          :columns="tableCol"
          :height="tableHeight"
          @on-show="showAccount"
          @on-switch="changeStatus"
          @on-select="setSelectList"
          @on-edit-account="onEditAccount"
        />

        <div class="pager">
          <el-pagination
            :current-page.sync="pageIndex"
            :page-size.sync="pageSize"
            :total="total"
            :page-sizes="[10, 30, 50, 100]"
            @current-change="changePageIndex"
            @size-change="changePageSize"
            layout="total, sizes, prev, pager, next, jumper"
            background
          >
          </el-pagination>
        </div>
      </Viewport>
    </div>

    <!-- 账号自动禁用弹窗 -->
    <account-disable-modal ref="accountForbidRef" v-model="showAccountDisableModal" :opData="dataSource.opData">
      <el-button type="primary" :loading="triggerLoading" @click="onAccountForbidManualTrigger">手动触发提醒</el-button>
      <el-button @click="showAccountDisableModal = false">取 消</el-button>
      <el-button type="primary" :loading="monitorBtnLoading" @click="onAccountForbidConfigConfirm">确 定</el-button>
    </account-disable-modal>

    <!-- 账号信息相关操作弹窗 -->
    <account-info-operate
      v-model="accountInfoModal"
      :dataSource="dataSource"
      :info="accountInfo"
      :type="accountInfoModalType"
      :loading="btnLoading"
      @on-ok="handleAccountEvent"
      @on-refresh-data="preload"
      @on-company="companyModal = true"
    />

    <!-- 公司主体操作弹窗 -->
    <company-body-operate v-model="companyModal" @on-refresh-data="preload" />

    <!-- 一键清理错误弹窗提示 -->

    <el-dialog :visible.sync="showClearMsg" title="提示" width="426px" :close-on-click-modal="false">
      <div v-if="clearErrorData && clearErrorData.length">
        <p>以下账号未能成功加入到清理任务，请查看具体失败原因：</p>
        <el-table border class="common-el-table-styles" max-height="220" :data="clearErrorData">
          <el-table-column width="100" prop="accountId" label="账号ID"></el-table-column>
          <el-table-column width="80" prop="row" label="行数"></el-table-column>
          <el-table-column prop="error" label="失败原因"></el-table-column>
        </el-table>
      </div>
      <p style="padding: 12px 0; font-weight: 700">
        <span v-if="clearErrorData && clearErrorData.length">其他</span>
        清理任务已经提交，清理完会发送邮件/钉钉通知
      </p>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="onClearMsgModalClose">知道了</el-button>
      </span>
    </el-dialog>

    <!-- 媒体素材管理账号配置弹窗 -->
    <material-account-config
      v-model="showMaterialConfigModal"
      :manage-accounts="dataSource.accountData"
      :loading="configLoading"
      @confirm="saveMaterialAccountConfig"
    />
  </Layout>
</template>

<script setup>
import { ref, getCurrentInstance, onMounted } from 'vue'
import AccountManageFilter from './components/account-manage-filter.vue'
import AccountManageTable from './components/account-manage-table.vue'
import AccountDisableModal from './components/account-disable-modal.vue'
import AccountInfoOperate from './components/account-info-operate.vue'
import CompanyBodyOperate from './components/company-body-operate.vue'
import MaterialAccountConfig from './components/material-account-config.vue'

import {
  useGlobalData,
  useAccountList,
  useAccountActions,
  useAccountModal,
  useSpiderMonitor,
  useClearAccounts,
  useMaterialAccountConfig
} from './useAccountManage'

const { proxy } = getCurrentInstance()

// Refs
const accountForbidRef = ref(null)
const selectList = ref([])

// 1. 全局配置
const { dataSource, preload } = useGlobalData()

// 2. 列表数据与分页
const {
  loading,
  pageIndex,
  pageSize,
  total,
  tableList,
  tableCol,
  tableHeight,
  params,
  sort,
  order,
  getData,
  changePageIndex,
  changePageSize
} = useAccountList()

// 3. 账号操作
const { companyModal, changeStatus, batchChangeStatus, accountExport, templateDown, importExcel } = useAccountActions(
  getData,
  selectList,
  proxy
)

// 4. 账号详情/编辑
const { accountInfoModal, accountInfoModalType, accountInfo, btnLoading, onAddAccount, onEditAccount, showAccount, handleAccountEvent } =
  useAccountModal(getData, proxy)

// 5. 自动禁用监控
// Wrap methods to pass ref
const {
  showAccountDisableModal,
  triggerLoading,
  monitorBtnLoading,
  openAccountDisableModal: _openAccountDisableModal,
  onAccountForbidConfigConfirm: _onAccountForbidConfigConfirm,
  onAccountForbidManualTrigger
} = useSpiderMonitor(proxy)

const openAccountDisableModal = () => _openAccountDisableModal(accountForbidRef)
const onAccountForbidConfigConfirm = () => _onAccountForbidConfigConfirm(accountForbidRef)

// 6. 清理账号
const {
  showClearMsg,
  clearErrorData,
  clearFileImportLoading,
  uploadUrl,
  handleUploadSuccess,
  handleUploadError,
  handleUploadChange,
  onClearMsgModalClose
} = useClearAccounts(proxy)

// 7. 媒体素材管理账号配置
const { showMaterialConfigModal, configLoading, openMaterialConfigModal, saveMaterialAccountConfig } = useMaterialAccountConfig(proxy)

// Helper
const setSelectList = (val) => {
  selectList.value = val
}

preload()
// Lifecycle
onMounted(() => {
  getData()
})
</script>

<style lang="scss" scoped>
.c-accountmana {
  width: 100%;
  height: 100%;
  padding: 8px 8px 0;
}

.pager {
  text-align: right;
}
</style>
